import React from "react"

import { Grid, Skeleton } from "@mui/material"
import { Tag, Tooltip, Typography } from "@synapse-analytics/synapse-ui"

import styles from "./InfoBlock.module.scss"

/**
 * Base interface for info container properties
 * @interface InfoContainerProps
 */
interface InfoContainerProps {
  /** Main title text */
  title?: string
  /** Tooltip text for the title */
  titleTooltip?: string
  /** Main content to display */
  text: string | number | React.ReactElement
  /** Tooltip text for the main content */
  textToolTip?: string
  /** Loading state flag */
  isLoading?: boolean
  /** Text transformation style */
  textTransform?: "capitalize" | "initial" | "uppercase" | "lowercase"
  /** Background color theme */
  variant?: "base" | "secondary"
  className?: string
}

/**
 * Properties for the default info container style
 * @interface DefaultInfoContainerProps
 * @extends InfoContainerProps
 */
interface DefaultInfoContainerProps extends InfoContainerProps {
  type: "default"
  /** Whether to show border */
  bordered?: boolean
  /** Text alignment */
  alignText?: "center" | "right" | "left"
  /** Color theme of the text */
  color?: "base" | "negative" | "positive"
  /** Color variant */
  variantColor?: 1 | 2
}

/**
 * Properties for the large info container style
 * @interface LargeInfoContainerProps
 * @extends InfoContainerProps
 */
interface LargeInfoContainerProps extends InfoContainerProps {
  type: "large"
  /** Optional tag configuration */
  tagProps?: {
    text: string | number
    variant: "green" | "red" | "yellow"
  }
}

type Props = LargeInfoContainerProps | DefaultInfoContainerProps

/**
 * Renders a default-sized info block with optional title, content, and styling
 * @param props - Default info block properties
 * @returns React element containing the default info block
 */
function DefaultInfoBlock(props: Readonly<DefaultInfoContainerProps>): React.ReactElement {
  const {
    title,
    titleTooltip,
    textTransform = "capitalize",
    text,
    bordered = true,
    isLoading,
    alignText = "left",
    color = "base",
    variantColor = 1,
    textToolTip,
    variant = "secondary",
    className,
  } = props

  return (
    <Grid container gap={0.75} className={className}>
      {title && (
        <Grid item xs={12}>
          <Typography variant="label-bold" tooltip={titleTooltip}>
            {title}
          </Typography>
        </Grid>
      )}

      <Grid
        item
        xs={12}
        className={`${styles.defaultBlockRoot} ${!bordered && styles.nonBordered} ${styles[`${variant}_colorVariant`]}`}
      >
        {!isLoading ? (
          textToolTip ? (
            <Tooltip title={textToolTip} placement="top">
              <Typography
                variant="p"
                align={alignText ? alignText : bordered ? "left" : "center"}
                textTransform={textTransform}
                noWrap
                color={color}
                variantColor={variantColor}
              >
                {text}
              </Typography>
            </Tooltip>
          ) : (
            <Typography
              variant="p"
              align={alignText ? alignText : bordered ? "left" : "center"}
              textTransform={textTransform}
              noWrap
              color={color}
              variantColor={variantColor}
            >
              {text}
            </Typography>
          )
        ) : (
          <Skeleton animation="wave" width="75%" />
        )}
      </Grid>
    </Grid>
  )
}

/**
 * Renders a large-sized info block with optional title, content, and tag
 * @param props - Large info block properties
 * @returns React element containing the large info block
 */
function LargeInfoBlock(props: Readonly<LargeInfoContainerProps>): React.ReactElement {
  const {
    title,
    titleTooltip,
    textTransform = "capitalize",
    text,
    isLoading = false,
    textToolTip,
    tagProps,
    variant = "base",
    className,
  } = props

  return (
    <Grid
      container
      item
      xs={12}
      className={`${styles.largeBlockRoot} ${styles[`${variant}_colorVariant`]} ${className}`}
    >
      {title && (
        <Grid item xs={12}>
          <Typography variant="a" tooltip={titleTooltip}>
            {title}
          </Typography>
        </Grid>
      )}

      {!isLoading ? (
        <Grid container item display={"flex"} justifyContent={"space-between"}>
          {textToolTip ? (
            <Tooltip title={textToolTip} placement="top">
              <Typography variant="h1-bold" textTransform={textTransform} noWrap>
                {text}
              </Typography>
            </Tooltip>
          ) : (
            <Typography variant="h1-bold" textTransform={textTransform} noWrap>
              {text}
            </Typography>
          )}

          {tagProps && (
            <Tag size="small" color={tagProps.variant} variant="filled">
              {tagProps.text}
            </Tag>
          )}
        </Grid>
      ) : (
        <Skeleton animation="wave" width="75%" height={"36px"} />
      )}
    </Grid>
  )
}

/**
 * InfoBlock component that displays information in either default or large format
 * with various styling options and loading states.
 *
 * @example
 * // Default usage
 * <InfoBlock
 *   type="default"
 *   title="Status"
 *   text="Active"
 *   bordered={true}
 * />
 *
 * @example
 * // Large usage with tag
 * <InfoBlock
 *   type="large"
 *   title="Revenue"
 *   text="$50,000"
 *   tagProps={{ text: "+15%", variant: "positive" }}
 * />
 *
 * @param props - Combined props for either default or large info block
 * @returns React element containing either a default or large info block
 */
export function InfoBlock(props: Readonly<Props>): React.ReactElement {
  return props.type === "default" ? <DefaultInfoBlock {...props} /> : <LargeInfoBlock {...props} />
}
